import React, {
  useState,
  useCallback,
  createRef,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';

import {
  DropdownDescription,
  Options,
  SelectValueWrap,
  SelectWrap,
} from './selectorStyle';
import Label from '@components/dataEntry/Input/Label';
import keyCode from '@typings/setting/keyCode.enum';
import SelectBtn from './SelectBtn';
import Scrollbar from './DropdownScrollbar';

import DropdownWrapper, {
  dropdownDirMap,
} from '@components/wrapper/DropdownWrapper';
import { AnimatePresence } from 'framer-motion';
import Avatar from '@components/dataDisplay/Avatar';

type SelectProps = {
  name?: string;
  defaultValue?: string;
  defaultKey?: string;
  selectedValue?: string;
  setSelectedValue?: any;
  setSelectedKey?: any;
  setSelectedAll?: any;
  selectList: ISelectItem[];
  label?: string;
  disabled?: boolean;
  loading?: boolean;
  maxHeight?: number;
  style?: any;
  dropdownStyle?: any;
  dropdownDirection?: keyof typeof dropdownDirMap;
  exceptAll?: boolean;
  size?: 'xl' | 'md' | 'sm';
  selectedKey?: string;
  placeholderValue?: string; // when no selectedKey, show this label (default '선택')
  optionFlexDirection?: 'row' | 'column';
  required?: boolean;
  /** true이면 드롭다운을 Portal로 렌더링하여 overflow 잘림 방지 */
  usePortal?: boolean;
};

export interface ISelectItem {
  id?: string;
  value: string;
  key?: string;
  order?: number;
  defaultValue?: boolean;
  thumbnail?: string;
  description?: string;
}

export function Select({
  label,
  name,
  selectList,
  selectedValue,
  setSelectedValue,
  selectedKey,
  setSelectedKey,
  setSelectedAll,
  defaultValue,
  disabled,
  loading,
  maxHeight,
  style,
  dropdownStyle,
  exceptAll,
  size = 'md',
  dropdownDirection = 'topRight',
  placeholderValue = '선택',
  optionFlexDirection = 'row',
  required,
  usePortal,
}: SelectProps) {
  const [isOpen, setisOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(exceptAll ? 1 : 0);

  // 유효 리스트: selectedKey가 없으면 맨 앞에 '선택' 자리 추가
  const effectiveList: ISelectItem[] = useMemo(() => {
    const base = Array.isArray(selectList) ? selectList : [];
    const noKey =
      selectedKey === undefined || selectedKey === null || selectedKey === '';
    if (noKey) {
      const hasEmpty = base.some(
        (it) =>
          (it.key ?? it.id ?? '') === '' ||
          (it.value ?? '') === placeholderValue,
      );
      if (!hasEmpty) {
        return [{ id: '', key: '', value: placeholderValue }, ...base];
      }
    }
    return base;
  }, [selectList, selectedKey, placeholderValue]);

  const [selectedItem, setSelectedItem] = useState<ISelectItem>(
    effectiveList[activeIndex],
  );
  const selectRef = useRef<HTMLDivElement>(null);
  const refs: any = useRef(
    Array.from({ length: effectiveList ? effectiveList.length : 0 }, () =>
      createRef(),
    ),
  );

  // keep refs length in sync with options length
  useEffect(() => {
    if (!effectiveList) return;
    refs.current = Array.from({ length: effectiveList.length }, () =>
      createRef(),
    );
  }, [effectiveList]);

  useEffect(() => {
    if (!effectiveList || activeIndex == null) return;
    const next = effectiveList[activeIndex] ?? effectiveList[0];
    if (next) setSelectedItem(next);
  }, [activeIndex, effectiveList]);

  const handleClickTrigger = useCallback(
    (e: any) => {
      if (!disabled && !loading) {
        e.stopPropagation();
        setisOpen(!isOpen);
      }
    },
    [isOpen, disabled, loading],
  );

  const handleSelect = useCallback(
    (e, index: number) => {
      console.log('🔍 handleSelect called:', {
        index,
        selectListLength: effectiveList?.length,
        selectedItem: effectiveList?.[index],
      });
      e.stopPropagation();

      if (index >= 0 && effectiveList && effectiveList[index]) {
        setActiveIndex(index);
        setSelectedItem(effectiveList[index]);

        console.log('✅ Setting values:', {
          item: effectiveList[index],
          hasSetSelectedAll: !!setSelectedAll,
          hasSetSelectedKey: !!setSelectedKey,
          hasSetSelectedValue: !!setSelectedValue,
        });

        if (setSelectedAll) setSelectedAll(effectiveList[index]);
        if (setSelectedKey) {
          setSelectedKey(
            effectiveList[index].key || effectiveList[index].id || '',
          );
        }
        if (setSelectedValue) setSelectedValue(effectiveList[index].value);
      } else {
        console.warn('⚠️ Invalid selection:', {
          index,
          listExists: !!effectiveList,
          itemExists: !!effectiveList?.[index],
        });
      }

      setisOpen(false);
    },
    [effectiveList, setSelectedAll, setSelectedKey, setSelectedValue],
  );

  // selectedKey 변화에 따라 activeIndex 동기화
  useEffect(() => {
    if (!effectiveList) return;
    let idx = activeIndex;
    const toStr = (v: any) => (v === undefined || v === null ? '' : String(v));
    const noKey =
      selectedKey === undefined || selectedKey === null || selectedKey === '';
    if (noKey) {
      idx = 0; // placeholder("선택") 고정
    } else {
      const target = toStr(selectedKey);
      const found = effectiveList.findIndex(
        (list) => toStr(list.key) === target || toStr(list.id) === target,
      );
      idx = found >= 0 ? found : 0;
    }
    if (idx == null || idx < 0) idx = 0;
    setActiveIndex(idx);
  }, [effectiveList, selectedKey]);

  function handleKeyDown(e: any) {
    const { key } = e;
    if (key === keyCode.ARROW_DOWN) {
      e.preventDefault();
      setActiveIndex((prevState) =>
        prevState < effectiveList!.length - 1 ? prevState + 1 : 0,
      );
    }

    if (key === keyCode.ARROW_UP) {
      e.preventDefault();
      setActiveIndex((prevState) =>
        prevState > 0 ? prevState - 1 : effectiveList!.length - 1,
      );
    }

    if (key === keyCode.ENTER || key === keyCode.TAB) {
      e.preventDefault();
      handleSelect(e, activeIndex);
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, activeIndex]);

  function handleClickOutside(e: any, dropdownNode) {
    if (selectRef.current && isOpen && dropdownNode) {
      const isClickOutside =
        !selectRef.current.contains(e.target) &&
        !dropdownNode.contains(e.target);

      if (isClickOutside) {
        setisOpen(false);
      }
    }
  }

  useEffect(() => {
    const scrollbar = document.getElementById(`dropdown-scrollbar-${name}`);

    if (scrollbar && activeIndex >= 0) {
      const heightOfPrevIndex = activeIndex * 52;
      const heightOfNowIndex = (activeIndex + 1) * 52 + 12;
      const heightofScrollStart = scrollbar.scrollTop;
      const heightOfScrollEnd = scrollbar.scrollTop + scrollbar.clientHeight;

      if (heightOfPrevIndex < heightofScrollStart) {
        scrollbar.scrollTop = heightOfPrevIndex;
      } else if (heightOfNowIndex > heightOfScrollEnd) {
        scrollbar.scrollTop =
          scrollbar.scrollTop + (heightOfNowIndex - heightOfScrollEnd);
      }
    }
  }, [activeIndex]);

  if (!effectiveList) return null;
  return (
    <SelectWrap ref={selectRef} style={{ ...style }}>
      {label && <Label required={required}>{label}</Label>}
      <SelectBtn
        onClick={handleClickTrigger}
        defaultValue={defaultValue}
        open={isOpen}
        disabled={disabled}
        loading={loading}
        size={size}
        item={selectedItem}
      />
      <AnimatePresence>
        {isOpen ? (
          <DropdownWrapper
            open={isOpen}
            style={{ ...dropdownStyle }}
            handleClickOutside={handleClickOutside}
            direction={dropdownDirection}
            usePortal={usePortal}
            triggerRef={usePortal ? selectRef : undefined}
          >
            <Scrollbar maxheight={maxHeight}>
              {effectiveList ? (
                effectiveList.map((list, index) => {
                  const { value, description, thumbnail } = list;
                  const stableKey = list.id || list.key || `${value}-${index}`;

                  return (
                    <Options
                      style={{ flexDirection: optionFlexDirection }}
                      key={stableKey}
                      ref={refs.current[index]}
                      isActiveIndex={activeIndex === index}
                      onClick={(e) => {
                        handleSelect(e, index);
                      }}
                      tabIndex={0}
                    >
                      <SelectValueWrap>
                        {thumbnail && (
                          <Avatar photoURL={thumbnail} size="20px" />
                        )}
                        {value}
                      </SelectValueWrap>
                      {description && (
                        <DropdownDescription>{description}</DropdownDescription>
                      )}
                    </Options>
                  );
                })
              ) : (
                <div>캐릭터가 없습니다.</div>
              )}
            </Scrollbar>
          </DropdownWrapper>
        ) : null}
      </AnimatePresence>
    </SelectWrap>
  );
}

export default React.memo(Select);
